import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";

import { getSession, signOut } from "../lib/auth";
import { CreateOrgForm } from "../components/CreateOrgForm";

/**
 * Landing screen for signed-in operators with zero `team_member` rows.
 *
 * Self-serve "Create your organization". On submit, POSTs to `/api/providers`;
 * on success, navigates straight to `/onboarding/survey` (step 1 of the new
 * wizard). No `sk_` key is minted here — the wizard handles that as an
 * optional step.
 *
 * Pre-conditions enforced server-side:
 *   - Operator must be signed in (auth middleware → 401 otherwise).
 *   - Operator's email must be verified (OAuth: yes; magic-link: after first
 *     click). Unverified emails get 403.
 *
 * Edge cases:
 *   - Already a member of at least one org: bounce to `/` (the SPA's
 *     `RequireAuth` would also catch this on the next render, but routing
 *     directly avoids a flash of the form).
 *   - No session at all: bounce to `/login`.
 */
export function Welcome() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ad-fg-3)",
          fontSize: 13,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!data?.user) {
    return <Navigate to="/login" replace />;
  }

  // Already a member of an org? They shouldn't be here.
  if (data.currentProviderId) {
    return <Navigate to="/" replace />;
  }

  async function onSignOut() {
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          padding: 32,
          background: "var(--ad-surface-1)",
          color: "var(--ad-fg-1)",
          border: "1px solid var(--ad-hairline)",
          borderRadius: 12,
          boxShadow: "var(--ad-card-shadow)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--ad-fg-3)",
            marginBottom: 6,
          }}
        >
          Welcome
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 8px" }}>
          Create your organization
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "var(--ad-fg-3)",
            margin: "0 0 20px",
          }}
        >
          Hi {data.user.name || data.user.email}, set up a new organization to
          get started. You'll be its owner. If a teammate sent you an
          invitation instead, open the invite email and click the link there.
        </p>

        <CreateOrgForm
          onSuccess={() => navigate("/onboarding/survey", { replace: true })}
        />

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--ad-hairline)",
            fontSize: 12,
            color: "var(--ad-fg-3)",
          }}
        >
          Signed in as {data.user.email}.{" "}
          <button
            type="button"
            onClick={onSignOut}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              color: "var(--rb-orange)",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
